import type { NoPIIType } from "utils/NoPII";

export enum ActionType {
  CLICK = "click",
  CLICK_BUTTON = "click_button",
  CLICK_LINK = "click_link",
  APV = "apv",
  VPV = "vpv",
  FOCUS = "focus",
  FORM_SUBMIT = "form_submit",
  SWIPE = "swipe",
  SCROLL = "scroll",
  STATE = "state",
  STATE_CHANGE = "state_change",
  CLICK_TAB = "click_tab",
  CLICK_PAGINATION = "click_pagination",
  UPLOAD_AUDIO = "audio_uploading",
  UPLOAD_VIDEO = "video_uploading",
  UPLOAD_IMAGE = "image_uploading",
  RECORD_AUDIO = "mic",
  RECORD_VIDEO = "webcam",
  ERROR = "error",
  EXPERIMENT_CHECK = "experiment",
}

// We can configure certain event types to be nullable or not
// in the Segment Tracking Plan.
export type AnalyticsSurface = SurfaceType | null;

export enum SurfaceType {
  NOT_FOUND_PAGE = "404_not_found_page",
  TESTING = "testing", // for general tests (jest)
  STORYBOOK = "storybook", // for storybook tests
  TEACHER_INBOX = "teacher_inbox",
  TEACHER_HOME = "teacher_home",
  TEACHER_HOME_BANNER = "teacher_home_banner",
  STUDENT_INBOX = "student_inbox",
  STUDENT_ARCHIVED = "student_archived",
  TEACHER_ARCHIVED = "teacher_archived",
  CUSTOM_QUESTION_COMPOSER = "custom_question_composer",
  SESSION_COMPOSER = "session_composer",
  SESSION_COMPOSER_UNSAVED_CHANGES_WARNING_MODAL = "session_composer_unsaved_changes_warning_modal",
  MESSAGE_COMPOSER = "message_composer",
  ACCOUNT_SETUP = "account_setup",
  ACCOUNT_PENDING_APPROVAL = "account_pending_approval",
  ACCOUNT_INVALID_DOMAIN = "account_invalid_domain",
  ACCOUNT_SETUP_SUCCESS_MODAL = "account_setup_success_modal",
  COOKIE_SETTINGS = "cookie_settings",
  NAVIGATION = "navigation",
  AUDIO_UPLOADING = "audio_uploading", // non-user interaction event types
  VIDEO_UPLOADING = "video_uploading", // non-user interaction event types
  IMAGE_UPLOADING = "image_uploading", // non-user interaction event types
  AUDIO_RECORDING = "audio_recording", // non-user interaction event types
  VIDEO_RECORDING = "video_recording", // non-user interaction event types
  CAMERA_DEMO = "camera_demo",
  GET_HELP = "get_help",
  CONTENT_LIBRARY = "content_library",
  // auth flow types
  USER_LOGIN = "user_login",
  USER_SIGNUP = "user_signup",
  SELECT_USER_TYPE = "select_user_type",
  STUDENT_SIGNUP_INFO = "student_signup_info",
  STUDENT_CONNECT_CODE_SIGNUP_PAGE = "student_connect_code_signup_page",
  TEACHER_PROFILE = "teacher_profile",
  STUDENT_PROFILE = "student_profile",
  TEACHER_WATCH_PAGE = "teacher_watch_page",
  STUDENT_WATCH_PAGE = "student_watch_page",
  COLLECTION_PAGE = "collection_page",
  STUDENT_ROSTER_PAGE = "student_roster_page",
  STUDENT_INTRODUCTION_PAGE = "student_introduction_page",
  TEACHER_ONBOARDING_PAGE = "teacher_onboarding_page",
  STUDENT_ONBOARDING_PAGE = "student_onboarding_page",
  CLASS_CODE_ERROR_PAGE = "class_code_error_page",
  TEACHER_RESPONSES_PAGE = "teacher_responses_page",
  TEACHER_SESSION_PAGE = "teacher_session_page",
  TEACHER_PARTICIPATION_PAGE = "teacher_participation_page",
  TEACHER_VIEWING_ACTIVE_CLASS_CODE_ERROR_MODAL = "link_is_for_students_modal",
  TEACHER_VIEWING_EXPIRED_CLASS_CODE_ERROR_MODAL = "link_is_expired_modal",
  STUDENT_ALREADY_ENROLLED_MODAL = "already_enrolled_modal",
  NEW_CLASS_CODE_WARNING_MODAL = "new_class_code_warning_modal",
  NEW_CLASS_CODE_SUCCESS_MODAL = "new_class_code_success_modal",
  REPORT_CONTENT_MODAL = "report_content_modal",
  REPORT_CONTENT_SUCCESS_MODAL = "report_content_success_modal",
  TEACHER_TOUR_OVERVIEW_MODAL = "teacher_tour_overview_modal",
  NEW_TEACHER_MODAL = "new_teacher_modal",
  TEACHER_TOUR_PAGE = "teacher_tour_page",
  WELCOME_QUESTION_PAGE = "welcome_question_page",
  WELCOME_QUESTION_WARNING_MODAL = "welcome_question_warning_modal",
  GENERIC_ERROR_MODAL = "generic_error_modal",
  TEACHER_NOTES_UNSAVED_CHANGES_WARNING_MODAL = "teacher_notes_unsaved_changes_warning_modal",
  TEACHER_MESSAGE_COMPOSER_UNSAVED_CHANGES_WARNING_MODAL = "teacher_message_composer_unsaved_changes_warning_modal",
  TEACHER_REFLECTION_EDIT_PAGE = "teacher_reflection_edit_page",
  ADD_STUDENTS = "add_students",
  ZENDESK_LIVE_CHAT = "zendesk_live_chat",
  WELCOME_LINK_GUIDE = "welcome_link_guide",
  REFLECTION_QUESTION_MODAL = "reflection_question_modal",
  SCHOOL_ROSTER = "school_roster",
  STUDENT_ACCOUNT_INFO_MODAL = "student_account_info_modal",
  TEACHER_ACCOUNT_INFO_MODAL = "teacher_account_info_modal",
  LIBRARY_FAVORITES = "library_favorites",
  MANAGE_STUDENT_GROUP_ROSTER_MODAL = "manage_student_group_roster_modal",
  STUDENT_GROUP_DETAIL_PAGE = "student_group_detail_page",
  SCHOOL_DATA_DASHBOARD = "school_data_dashboard",
  ANNOUNCEMENT_BANNER = "announcement_banner",
  CLEVER_SECTION_IMPORT_MODAL = "clever_section_import_modal",
  POLICY_PAGES = "policy_pages",
  TEACHER_APP_GENERAL = "teacher_app_general",
  STUDENT_APP_GENERAL = "student_app_general",
  GET_TO_KNOW_ALONG_MODAL = "get_to_know_along_modal",
  GROUP_REPLY_MODAL = "group_reply_modal",
  MOCK_STUDENT_BANNER = "mock_student_banner",
  WATCH_QUEUE = "watch_queue",
  LIBRARY_SEARCH = "library_search",
  LIBRARY_SEARCH_PAGE = "library_search_page",
}

const Routes = {
  Teacher: {
    VIEWED_CONTENT_LIBRARY_SEARCH_PAGE: "viewed_content_library_search_page",
  },
} as const;

// Enum name is the SurfaceType name and values are the EventName values
const NotFoundPage = {
  CLICKED_BACK_TO_HOME: "clicked_back_to_home",
} as const;
const Testing = {
  STORYBOOK: "mock_storybook_event",
  TEST: "mock_test_event",
} as const;
const StudentInbox = {
  VIEWED_REFLECTION_NOT_FOUND_ERROR_MODAL:
    "viewed_reflection_not_found_error_modal",
  CLICKED_CLOSE_REFLECTION_NOT_FOUND_ERROR_MODAL:
    "clicked_close_reflection_not_found_error_modal",
  VIEWED_SESSION: "viewed_session",
};
const TeacherInbox = {
  CLICKED_PLAY_ALL_NEW_REFLECTIONS: "clicked_play_all_new_reflections",
  VIEWED_SESSION: "viewed_session",
  RETRIED_PLAYING_VIDEO: "retried_playing_video",
  TEACHER_INVENTORY: "teacher_inventory",
  CLICKED_COLLECTION: "clicked_collection",
  CLICKED_RECOMMENDED_COLLECTION: "clicked_recommended_collection",
  CLICKED_NEW_REFLECTIONS: "clicked_new_reflections",
  CLICKED_MORE_NEW_REFLECTIONS: "clicked_more_new_reflections",
  VIEWED_NEW_REFLECTION_TRAY: "viewed_new_reflection_tray",
  CLICKED_GO_TO_CREATE_GROUP_MODAL: "clicked_go_to_create_group_modal",
  COPIED_CLASS_CONNECT_CODE: "copied_class_connect_code",
  COPIED_GROUP_CONNECT_CODE: "copied_group_connect_code",
  CLICKED_GROUP_CARD: "clicked_group_card",
} as const;
const TeacherHomeBanner = {
  CLICKED_ALL_CAUGHT_UP_LIBRARY_BUTTON: "clicked_all_caught_up_library_button",
} as const;
const WatchQueue = {
  CLICKED_CLOSE_ON_WATCH_QUEUE_MODAL: "clicked_close_on_watch_queue_modal",
  CLICKED_GO_TO_ALL_RESPONSES: "clicked_go_to_all_responses",
  CLICKED_MARK_AS_READ: "clicked_mark_as_read",
  CLICKED_VIEW_IN_INSIGHTS_PAGE: "clicked_view_in_insights_page",
  CLICKED_VIEW_UNREAD_SESSIONS_BUTTON: "clicked_view_unread_sessions_button",
  CLICKED_WATCH_QUEUE_CARD_VIEW_SESSION:
    "clicked_watch_queue_card_view_session",
  CLICKED_PREV_PAGE_ON_WATCH_QUEUE_MODAL:
    "clicked_prev_page_on_watch_queue_modal",
  CLICKED_NEXT_PAGE_ON_WATCH_QUEUE_MODAL:
    "clicked_next_page_on_watch_queue_modal",
} as const;
const Composer = {
  VIEWED_SESSION_COMPOSER_UNSAVED_CHANGES_WARNING_MODAL:
    "viewed_session_composer_unsaved_changes_warning_modal",
  CLICKED_REFLECTION_TYPE: "clicked_reflection_type",
  VIEWED_CREATE_SESSION_SUBMIT: "viewed_create_session_submit",
} as const;
const ComposerManager = {
  PUBLISHED_SESSION: "published_session",
  SUBMITTED_SESSION: "submitted_session",
} as const;
const Navigation = {
  CLICKED_PROFILE: "clicked_profile",
  CLICKED_HOME_LINK: "clicked_home_link",
  CLICKED_CONTENT_LIBRARY_LINK: "clicked_content_library_link",
  CLICKED_STUDENT_ROSTER: "clicked_student_roster",
  CLICKED_SCHOOL_ROSTER: "clicked_school_roster",
  CLICKED_SCHOOL_DATA_DASHBOARD: "clicked_school_data_dashboard",
  CLICKED_RESOURCES: "clicked_resources",
  CLICKED_ACCOUNT_DROPDOWN: "clicked_account_dropdown",
  CLICKED_RESPONSES: "clicked_responses",
  CLICKED_STUDENT_VIEW: "clicked_go_to_student_view",
  CLICKED_EXIT_STUDENT_VIEW: "clicked_exit_student_view",
  CLICKED_LOGOUT: "logged_out",
} as const;
const ContentLibrary = {
  CLICKED_ACTIVITY: "clicked_activity",
  CLICKED_CLOSE_ON_REFLECTION_INFO_MODAL:
    "clicked_close_on_reflection_info_modal",
  CLICKED_CLOSE_ON_RESOURCE_INFO_MODAL: "clicked_close_on_resource_info_modal",
  CLICKED_CONTENT_PARTNERS_LINK: "clicked_content_partners_link",
  CLICKED_FAVORITE_CONTENT: "clicked_favorite_content",
  CLICKED_SUBMIT_ON_REFLECTION_INFO_MODAL:
    "clicked_submit_on_reflection_info_modal",
  CLICKED_SUBMIT_ON_RESOURCE_INFO_MODAL:
    "clicked_submit_on_resource_info_modal",
  CLICKED_UNFAVORITE_CONTENT: "clicked_unfavorite_content",
  SELECTED_SESSION_TOPIC: "selected_session_topic",
  VIEWED_REFLECTION_INFO_MODAL: "viewed_reflection_info_modal",
  VIEWED_RESOURCE_INFO_MODAL: "viewed_resource_info_modal",
  OPENED_REFLECTION_COMPOSER: "opened_reflection_composer",
  CLICKED_GO_TO_FAVORITES_LIBRARY: "clicked_go_to_favorites_library",
} as const;
const AccountSetup = {
  VIEWED_SETUP_ERROR: "viewed_setup_error",
  CLICKED_NEXT_ON_CONFIRM_SCHOOL_FORM: "clicked_next_on_confirm_school_form",
  CLICKED_BACK_TO_EXPLORE: "clicked_back_to_explore",
  CLICKED_START_ONBOARDING: "clicked_start_onboarding",
  CLICKED_CONTACT_SUPPORT: "clicked_contact_support",
  CLICKED_NEXT_ON_LEADER_LOOP: "clicked_next_on_leader_loop",
  CLICKED_AGREED_ON_GUARDIAN_LOOP: "clicked_agreed_on_guardian_loop",
  CLICKED_SUPPORT_EMAIL: "clicked_support_email",
} as const;
const UserLogin = {
  VIEWED_LOGIN_ERROR: "viewed_login_error",
} as const;
const CleverImportStartedModal = {
  VIEWED_CLEVER_IMPORT_START_MODAL: "viewed_clever_import_start_modal",
  CLICKED_CLOSE_ON_CLEVER_IMPORT_START_MODAL:
    "clicked_close_on_clever_import_start_modal",
  CLICKED_GO_TO_SIGN_IN_ON_CLEVER_IMPORT_START_MODAL:
    "clicked_go_to_sign_in_on_clever_import_start_modal",
  CLICKED_ACKNOWLEDGE_ON_CLEVER_IMPORT_START_MODAL:
    "clicked_acknowledge_on_clever_import_start_modal",
} as const;
const CleverSectionImportModal = {
  VIEWED_CLEVER_SECTION_IMPORT_MODAL: "viewed_clever_section_import_modal",
  CLICKED_CLOSE: "clicked_close",
  FOCUSED_SEARCH: "focused_search",
  CLICKED_IMPORT_SECTIONS: "clicked_import_sections",
  CLICKED_GO_TO_CREATE_GROUP_MODAL: "clicked_go_to_create_group_modal",
} as const;
const StudentGroupDetail = {
  CLICKED_BACK_TO_STUDENT_GROUPS_PAGE: "clicked_back_to_student_groups_page",
  CLICKED_GO_TO_SIGN_IN: "clicked_go_to_sign_in",
} as const;
const StudentRosterPage = {
  CLICKED_ADD_GROUPS_BUTTON: "clicked_add_groups_button",
  CLICKED_ARCHIVE_LEARN_MORE_ON_DELETE_GROUP_MODAL:
    "clicked_archive_learn_more_on_delete_group_modal",
  CLICKED_ARCHIVE_LEARN_MORE: "clicked_archive_learn_more",
  CLICKED_BACK_TO_TEACHER_ACCOUNT_INFO_MODAL:
    "clicked_back_to_teacher_account_info_modal",
  CLICKED_CANCEL_CREATE_STUDENT_GROUP: "click_cancel_create_student_group",
  CLICKED_CLOSE_ON_DELETE_GROUP_MODAL: "clicked_close_on_delete_group_modal",
  CLICKED_CREATE_STUDENT_GROUP: "clicked_create_student_group",
  CLICKED_DELETE_ON_DELETE_GROUP_MODAL: "clicked_delete_on_delete_group_modal",
  CLICKED_DELETE_STUDENT_GROUP: "clicked_delete_student_group",
  CLICKED_DENY_ON_DELETE_GROUP_MODAL: "clicked_deny_on_delete_group_modal",
  CLICKED_GO_TO_CREATE_GROUP_MODAL: "clicked_go_to_create_group_modal",
  CLICKED_GROUP_LINK: "clicked_group_link",
  CLICKED_GROUP_SUBMENU: "clicked_group_submenu",
  CLICKED_HELP_DESK_FROM_SECTION_SYNC_BANNER:
    "clicked_help_desk_from_section_sync_banner",
  CLICKED_RESOURCE: "clicked_resource",
  CLICKED_SORT_TABLE: "clicked_sort_table",
  FOCUSED_GROUP_NAME: "focused_group_name",
  VIEWED_DELETE_GROUP_MODAL: "viewed_delete_group_modal",
} as const;
const TeacherAccountInfoModal = {
  CLICKED_CLOSE: "clicked_close",
  CLICKED_CONFIRM: "clicked_confirm",
  CLICKED_TAB: "clicked_tab",
  CLICKED_WELCOME_STUDENTS_TO_ALONG_SLIDES:
    "clicked_welcome_students_to_along_slides",
  VIEWED_TEACHER_ACCOUNT_INFO_MODAL: "viewed_teacher_account_info_modal",
} as const;
const StudentTable = {
  CLICKED_SORT_TABLE: "clicked_sort_table",
} as const;
const CleverImportInstructionsModule = {
  CLICKED_OPEN_CLEVER_SECTION_SYNC_MODAL:
    "clicked_open_clever_section_sync_modal",
  CLICKED_GO_TO_CREATE_GROUP_MODAL: "clicked_go_to_create_group_modal",
} as const;
const StudentConnectCodeSignupPage = {
  LOGGED_OUT: "logged_out",
  CLICKED_SIGN_IN: "clicked_sign_in",
  CLICKED_SIGN_UP: "clicked_sign_up",
  FOCUSED_TYPE_CONNECT_CODE: "focused_type_connect_code",
  CLICKED_HELP_CENTER_LINK: "clicked_help_center_link",
  CLICKED_GO_TO_ALONG_APP: "clicked_go_to_along_app",
} as const;
const RouteLeavingGuardModal = {
  CLICKED_DENY: "clicked_deny",
  CLICKED_CONFIRM: "clicked_confirm",
} as const;
const TeacherWatchPage = {
  SHOW_MORE_STUDENT_SESSIONS: "show_more_student_sessions",
  CLICKED_SESSION_STATE_TAB: "clicked_session_state_tab",
  CLICKED_MESSAGE_STUDENT_ICON: "clicked_message_student_icon",
  CLICKED_COPY_ICON_ON_PROMPT_LINK: "clicked_copy_icon_on_prompt_link",
} as const;
const PolicyPages = {
  CLICKED_HOME_LINK: "clicked_home_link",
  CLICKED_PRIVACY_POLICY_LINK: "clicked_privacy_policy_link",
  CLICKED_USER_AGREEMENT_LINK: "clicked_user_agreement_link",
  CLICKED_CODE_OF_CONDUCT_LINK: "clicked_code_of_conduct_link",
  CLICKED_STUDENT_PRIVACY_NOTICE_LINK: "clicked_student_privacy_notice_link",
  CLICKED_EMAIL_SUPPORT_LINK: "clicked_email_support_link",
  CLICKED_EMAIL_REPORT_LINK: "clicked_email_report_link",
  CLICKED_EMAIL_PRIVACY_LINK: "clicked_email_privacy_link",
  CLICKED_EMAIL_SECURITY_LINK: "clicked_email_security_link",
  CLICKED_EMAIL_LEGAL_LINK: "clicked_email_legal_link",
} as const;
const ConnectCodeInstructionModule = {
  CLICKED_COPY_CONNECT_CODE_INSTRUCTIONS:
    "clicked_copy_connect_code_instructions",
  CLICKED_GET_NEW_CONNECT_CODE: "clicked_get_new_connect_code",
  CLICKED_CONNECT_CODE_STUDENT_PAGE_LINK:
    "clicked_connect_code_student_page_link",
  CLICKED_ROSTER_IN_GROUPS_LINK: "clicked_roster_in_groups_link",
};
const StudentProfileAsTeacher = {
  CLICKED_BACK_TO_STUDENTS_PAGE: "clicked_back_to_students_page",
  CLICKED_PAST_REFLECTION: "clicked_past_reflection",
  CLOSED_LIST_OF_OTHER_TEACHERS_MODAL: "closed_list_of_other_teachers_modal",
  OPENED_LIST_OF_OTHER_TEACHERS_MODAL: "opened_list_of_other_teachers_modal",
  CLICKED_STUDENT_PROFILE: "clicked_student_profile",
  CLICKED_GO_TO_LIBRARY: "clicked_go_to_library",
};
const TeacherAppRoutes = {
  VIEWED_TEACHER_SESSIONS_TAG_REDIRECT_PAGE:
    "viewed_teacher_sessions_tag_redirect_page",
};
const StudentAppRoutes = {
  VIEWED_STUDENT_SESSION_TAG_REDIRECT_PAGE:
    "viewed_student_session_tag_redirect_page",
};

const MockStudentBanner = {
  CLICKED_EXIT_STUDENT_VIEW: "clicked_exit_student_view",
};

const ComposerV2 = {
  CLICKED_NEXT: "clicked_next",
  TYPED_QUESTION_TEXT: "typed_question_text",
  TYPED_MULTIPLE_CHOICE_OPTION_TEXT: "typed_multiple_choice_option_text",
  TYPED_NOTE_TEXT: "typed_note_text",
  CLICKED_RERECORD: "clicked_rerecord",
  CLICKED_SKIP_RECORDING: "clicked_skip_recording",
  CLICKED_FINISHED_SETTING_UP: "clicked_finish_setting_up",
  CLICKED_SELECT_QUESTION_TYPE: "clicked_select_question_type",
  CLICKED_CUSTOMIZE_QUESTION: "clicked_customize_question",

  CLICKED_CLOSE_SCHEDULED_PROMPT_SUCCESS_MODAL:
    "clicked_close_scheduled_prompt_success_modal",
  VIEWED_SCHEDULED_PROMPT_SUCCESS_MODAL:
    "viewed_scheduled_prompt_success_modal",
  CLICKED_COPY_ICON_ON_PROMPT_SUCCESS_MODAL:
    "clicked_copy_icon_on_prompt_success_modal",
  CLICKED_COPY_ICON_ON_SCHEDULED_PROMPT_SUCCESS_MODAL:
    "clicked_copy_icon_on_scheduled_prompt_success_modal",
  CLICKED_OK_ON_SCHEDULED_PROMPT_SUCCESS_MODAL:
    "clicked_ok_on_scheduled_prompt_success_modal",
  CLICKED_OK_ON_CANCEL_SCHEDULED_PROMPT_SUCCESS_MODAL:
    "clicked_ok_on_cancel_scheduled_prompt_success_modal",
  CLICKED_CLOSE_SCHEDULED_PROMPT_CANCELED_MODAL:
    "clicked_close_scheduled_prompt_canceled_modal",
  VIEWED_SCHEDULED_PROMPT_CANCELED_MODAL:
    "viewed_scheduled_prompt_canceled_modal",
  CLICKED_OK_ON_CANCEL_SCHEDULED_PROMPT_CANCELED_MODAL:
    "clicked_ok_on_cancel_scheduled_prompt_canceled_modal",
};

const TeacherSessionPage = {
  CLICKED_LOAD_MORE_MESSAGES_BUTTON: "clicked_load_more_messages_button",
  CLICKED_GO_TO_GROUP_REPLY_MODAL: "clicked_go_to_group_reply_modal",
  TOGGLED_HIDE_RESPONSES: "toggled_hide_responses",
  CLICKED_ADD_STUDENTS_TO_REFLECTION: "clicked_add_students_to_reflection",
  VIEWED_ADD_STUDENTS_MODAL: "viewed_add_students_modal",
  CLICKED_CLOSE_ADD_SESSION_STUDENTS_MODAL:
    "clicked_close_add_session_students_modal",
  ADDED_STUDENT_TO_AUDIENCE: "added_student_to_audience",
  REMOVED_STUDENT_FROM_AUDIENCE: "removed_student_from_audience",
  ADD_SESSION_STUDENTS_SUBMITTED: "add_session_students_submitted",
  ADD_SESSION_STUDENTS_COMPLETED: "add_session_students_completed",
  CLICKED_TEACHER_GUIDANCE_LINK: "clicked_teacher_guidance_link",
  CLICKED_COPY_STUDENT_SESSION_LINK_BUTTON:
    "clicked_copy_student_session_link_button",
  TOGGLED_SHOW_NOTE: "toggled_show_note",
  CLICKED_CLOSED_CANCEL_SCHEDULE_REFLECTION_MODAL:
    "clicked_closed_cancel_schedule_reflection_modal",
  VIEWED_CANCEL_SCHEDULE_REFLECTION_MODAL:
    "viewed_cancel_schedule_reflection_modal",
  CLICKED_CANCEL_SCHEDULE_REFLECTION: "clicked_cancel_schedule_reflection",
  CLICKED_RESCHEDULE_REFLECTION: "clicked_reschedule_reflection",
  CLICKED_REQUEST_CANCEL_SCHEDULED_REFLECTION:
    "clicked_request_cancel_scheduled_reflection",
  CLICKED_SEND_SCHEDULED_REFLECTION_NOW:
    "clicked_send_scheduled_reflection_now",
};

const GroupReplyModal = {
  CLICKED_REPLY_TO_GROUP: "clicked_reply_to_group",
  CLICKED_DONE: "clicked_done",
  CLICKED_CLOSE_MODAL: "clicked_close_modal",
  PUBLISHED_MESSAGE: "published_message",
  CLICKED_REPLY_GROUP_DROPDOWN: "clicked_reply_group_dropdown",
  CLICKED_REPLY_GROUP_DROPDOWN_OPTION: "clicked_reply_group_dropdown_option",
};

const TeacherResponsesPage = {
  CLICKED_ASK_NEW_QUESTION: "clicked_ask_new_question",
  CLICKED_LOAD_MORE_RESPONSES_BUTTON: "clicked_load_more_responses_button",
  CLICKED_GO_TO_STUDENT_ROSTER: "clicked_go_to_student_roster",
  CLICKED_CHANGE_SENT_QUESTION_VIEW: "clicked_change_sent_question_view",
};

const TeacherMessageComposer = {
  VIEWED_TEACHER_MESSAGE_COMPOSER_UNSAVED_CHANGES_WARNING_MODAL:
    "viewed_teacher_message_composer_unsaved_changes_warning_modal",
};

const StudentGroupDropdown = {
  CLICKED_STUDENT_GROUP_DROPDOWN: "clicked_student_group_dropdown",
  CLICKED_GROUP_RESPONSES_LINK: "clicked_group_responses_link",
  CLICKED_ADD_GROUPS_BUTTON: StudentRosterPage.CLICKED_ADD_GROUPS_BUTTON,
};

const StudentSession = {
  CLICKED_SESSION_CARD: "clicked_session_card",
  CLICKED_RESPONSE_SUBMENU: "clicked_response_submenu",
  CLICKED_COPY_QUESTION_LINK: "clicked_copy_question_link",
};

const MultipleChoiceResponseTable = {
  CLICKED_STUDENT_MESSAGE: "clicked_student_message",
};

const OpenResponseTable = {
  CLICKED_STUDENT_MESSAGE: MultipleChoiceResponseTable.CLICKED_STUDENT_MESSAGE,
};

const Header = {
  CLICKED_PAGE_HEADER_BACK_BUTTON: "clicked_page_header_back_button",
};

const Messages = {
  VIEWED_MESSAGE: "viewed_message",
};

const ResponsesNavigation = {
  CLICKED_NEXT_STUDENT_SESSION: "clicked_next_student_session",
  CLICKED_PREVIOUS_STUDENT_SESSION: "clicked_previous_student_session",
};

const Carousel = {
  CLICKED_PREVIOUS_CAROUSEL: "clicked_previous_carousel",
  CLICKED_NEXT_CAROUSEL: "clicked_next_carousel",
};

const Error = {
  CLICKED_REFRESH: "clicked_refresh",
  CLICKED_CONTACT_SUPPORT_FROM_ERROR: "clicked_contact_support_from_error",
};

const LibrarySearch = {
  CLICKED_SEARCH: "clicked_search",
  SUBMITTED_SEARCH: "submitted_search",
  CLICKED_SEARCH_SUGGESTION_LINK: "clicked_search_suggestion_link",
  CLICKED_WRITE_YOUR_OWN_QUESTION: "clicked_write_your_own_question",
  CLICKED_BROWSE_LIBRARY: "clicked_browse_library",
};

const BetaLabel = {
  CLICKED_SHARE_YOUR_FEEDBACK: "clicked_share_your_feedback",
};

// Cant created nested enums but this is sufficient
export const EventName = {
  NotFoundPage,
  Testing,
  TeacherInbox,
  TeacherHomeBanner,
  WatchQueue,
  Composer,
  ComposerV2,
  ComposerManager,
  StudentInbox,
  Navigation,
  ContentLibrary,
  AccountSetup,
  UserLogin,
  CleverImportStartedModal,
  CleverSectionImportModal,
  StudentGroupDetail,
  StudentRosterPage,
  TeacherAccountInfoModal,
  CleverImportInstructionsModule,
  StudentConnectCodeSignupPage,
  RouteLeavingGuardModal,
  TeacherWatchPage,
  PolicyPages,
  ConnectCodeInstructionModule,
  StudentProfileAsTeacher,
  StudentTable,
  TeacherSessionPage,
  GroupReplyModal,
  TeacherResponsesPage,
  TeacherMessageComposer,
  StudentGroupDropdown,
  StudentSession,
  MultipleChoiceResponseTable,
  OpenResponseTable,
  Header,
  MockStudentBanner,
  TeacherAppRoutes,
  StudentAppRoutes,
  Messages,
  ResponsesNavigation,
  Carousel,
  Error,
  LibrarySearch,
  Routes,
  BetaLabel,
} as const;

const EventNames = Object.values(EventName)
  .map((enumObject) => Object.values(enumObject))
  .flat(1);
export type EventNameType = (typeof EventNames)[number];

export type OnboardingState =
  | "email_is_rejected"
  | "email_is_pending"
  | "email_is_verified"
  | "can_have_students"
  | "does_have_students";

export interface GroupUsageMetadata {
  sessionId: NoPIIType;
  studentGroupId: NoPIIType;
  numStudentGroupMembers: number;
  numStudentsAdded?: number;
  numAdditionalStudentsAdded?: number;
  numExistingStudents?: number;
}

export interface GroupSelectionsMetadata {
  studentGroupId: NoPIIType;
  selectedStudentIds: NoPIIType[];
  fullGroupSelected: boolean;
}

export interface StickerAnalyticMetadata {
  name: NoPIIType;
  language: NoPIIType;
  sentiment: NoPIIType;
}

export interface ExtraMetadata {
  [key: string]:
    | number
    | boolean
    | NoPIIType
    | GroupUsageMetadata[]
    | StickerAnalyticMetadata[]
    | GroupSelectionsMetadata[];
  groupsUsageInfo?: GroupUsageMetadata[];
  stickers?: StickerAnalyticMetadata[];
}

export const AnalyticsPropertiesValues = {
  HEADER: "header",
  FOOTER: "footer",
};

// If user IDs are added to the top level, make sure to update
// `convertPropertyUserIds()` in utils/analytics.tsx
export interface AnalyticsProperties {
  studentId?: NoPIIType;
  studentSessionId?: string;
  studentGroupId?: string;
  studentGroupName?: string;
  messageId?: string;
  sessionId?: string | null;
  sessionOwnerId?: string | null;
  topicId?: string | null;
  topicName?: string | null;
  href?: string;
  onboardingState?: OnboardingState;
  dashboardName?: string;
  extraMetadata?: ExtraMetadata;
  location?: string;
  cntNewResponses?: number;
  cntRespondents?: number;
  cntAvailableStudents?: number;
}
